import useEventListener from '@hooks/use-event-listener';
import buildClassName from '@utils/build-class-name';
import type { GetProps } from 'antd';
import { Button } from 'antd';
import type { FocusEventHandler, MouseEventHandler } from 'react';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import styles from './ForceButton.module.scss';

interface ForceButtonProps extends GetProps<typeof Button> {
  forceableDisabled?: boolean;
}

const ForceButton = forwardRef<HTMLSpanElement, ForceButtonProps>(function ForceButton(
  {
    disabled = false,
    forceableDisabled = false,
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    style,
    children,
    ...restProps
  },
  ref,
) {
  const [isDisabled, setIsDisabled] = useState(disabled || forceableDisabled);
  const isShiftPressedRef = useRef(false);
  // Keep internal state in sync when the `disabled` prop changes externally
  useEffect(() => {
    setIsDisabled(disabled || forceableDisabled);
  }, [disabled, forceableDisabled]);

  // Handle pressing shift when already hovered or focused
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  useEventListener('keydown', (event) => {
    isShiftPressedRef.current = event.shiftKey;
    if ((isHovered || isFocused) && event.shiftKey && !disabled) {
      setIsDisabled(false);
    }
  });
  useEventListener('keyup', (event) => {
    if (event.key === 'Shift') {
      isShiftPressedRef.current = false;
      setIsDisabled(disabled || forceableDisabled);
    }
  });

  const handleMouseEnter = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      setIsHovered(true);
      if (isShiftPressedRef.current && !disabled) {
        setIsDisabled(false);
      }

      onMouseEnter?.(event);
    },
    [disabled, onMouseEnter],
  );
  const handleMouseLeave = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      setIsHovered(false);
      setIsDisabled(disabled || forceableDisabled);

      onMouseLeave?.(event);
    },
    [disabled, forceableDisabled, onMouseLeave],
  );
  const handleFocus = useCallback<FocusEventHandler<HTMLElement>>(
    (event) => {
      setIsFocused(true);
      if (isShiftPressedRef.current && !disabled) {
        setIsDisabled(false);
      }

      onFocus?.(event);
    },
    [disabled, onFocus],
  );
  const handleBlur = useCallback<FocusEventHandler<HTMLElement>>(
    (event) => {
      setIsFocused(false);
      setIsDisabled(disabled || forceableDisabled);

      onBlur?.(event);
    },
    [disabled, forceableDisabled, onBlur],
  );

  return (
    <span
      ref={ref} // Attach the ref to the span so Tooltip positions correctly
      onBlur={handleBlur}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        display: 'inline-block',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      tabIndex={isDisabled && forceableDisabled && !disabled ? 0 : undefined}
    >
      <Button
        {...restProps}
        className={buildClassName(
          !isDisabled && isHovered && styles.btnHovered,
          !isDisabled && isFocused && styles.btnFocused,
        )}
        title={isDisabled && forceableDisabled && !disabled ? 'Hold Shift & Hover to Force-enable' : undefined}
        disabled={isDisabled}
        style={{
          // Stop button from blocking mouse events up to the span wrapper
          pointerEvents: isDisabled ? 'none' : 'auto',
          ...style,
        }}
      >
        {children}
      </Button>
    </span>
  );
});

export default ForceButton;
